import { useCallback, useEffect, useState } from 'react';

const KEY = 'gc_favorites';

const read = (): string[] => {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

// Favorites stored in localStorage; a storage event + custom event keep every
// mounted hook instance in sync across tabs and within this tab.
export const useFavorites = () => {
  const [favorites, setFavorites] = useState<string[]>(read);

  useEffect(() => {
    const sync = () => setFavorites(read());
    window.addEventListener('storage', sync);
    window.addEventListener('gc:favorites', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('gc:favorites', sync);
    };
  }, []);

  const toggleFavorite = useCallback((id: string | number) => {
    const sid = String(id);
    setFavorites((prev) => {
      const next = prev.includes(sid) ? prev.filter((f) => f !== sid) : [...prev, sid];
      localStorage.setItem(KEY, JSON.stringify(next));
      window.dispatchEvent(new Event('gc:favorites'));
      return next;
    });
  }, []);

  const isFavorite = useCallback(
    (id: string | number) => favorites.includes(String(id)),
    [favorites]
  );

  return { favorites, toggleFavorite, isFavorite };
};
